import { State } from "redux/store";
import { createSelector } from "@reduxjs/toolkit";
import {
  EnrichmentModel,
  EnrichmentPreview,
  EnrichmentStatus,
  EnrichmentModelForPreview,
  EnrichmentModelOption,
  EnrichmentModelSelectedStatus,
  ModelSelectionData,
} from "types";
import { getDatasets } from "redux/Datasets/selectors";

const state = (state: State) => state.enrichment;
const singleDatasetState = (state: State) => state.singleDataset;

export const getEnrichmentPreviewLoading = createSelector(
  state,
  (state): boolean => {
    return state.isPreviewLoading;
  },
);

const getModelsList = createSelector(state, (state): EnrichmentModel[] => {
  return state.modelList;
});

export const getEnrichmentContextId = createSelector(
  state,
  (state): null | string => {
    return state.enrichmentContextId;
  },
);

export const getEnrichmentPreviews = createSelector(
  state,
  (state): EnrichmentPreview[] => {
    return state.previewData;
  },
);

export const getUserSelectedPreviewModels = createSelector(
  state,
  (state): EnrichmentModel[] => {
    return state.userSelectedPreviewModels;
  },
);

export const getPreviousDatasetEnrichmentModels = createSelector(
  state,
  (state): EnrichmentModel[] => {
    return state.previousDatasetEnrichmentModels;
  },
);

export const getEnrichedDatasetId = createSelector(
  state,
  (state): null | string => {
    return state.enrichedDatasetId;
  },
);

export const getModelSelectionData = createSelector(
  state,
  (state): ModelSelectionData | null => {
    return state.modelSelectionData;
  },
);

export const getPreviewModels = createSelector(
  getPreviousDatasetEnrichmentModels,
  getUserSelectedPreviewModels,
  getEnrichedDatasetId,
  getModelSelectionData,
  (
    previousDatasetEnrichmentModels: EnrichmentModel[],
    userSelectedModels: EnrichmentModel[],
    enrichedDatasetId: string | null,
    modelSelectionData: ModelSelectionData | null,
  ): EnrichmentModelForPreview[] => {
    const previousModelsForPreview = previousDatasetEnrichmentModels
      .filter((model) => !model.replaced)
      .map((model) => {
        return {
          ...model,
          deletable: {
            value: false,
            reason: "Previously used model. Removal not allowed",
          },
          editable: {
            value: false,
            reason: "Previously used model. Cannot change the prompt",
          },
        };
      });

    const userSelectedModelsForPreview = userSelectedModels.map((model) => {
      return {
        ...model,
        deletable: { value: true, reason: "" },
        editable: { value: true, reason: "" },
      };
    });

    //Enriichment local storage
    let localModels: any[] = [];
    if (enrichedDatasetId) {
      localModels = modelSelectionData?.[enrichedDatasetId!]?.models || [];

      if (localModels.length > 0) {
        localModels = localModels
          .filter(
            (localModel) =>
              !userSelectedModelsForPreview.some(
                (userModel) => userModel.id === localModel.id,
              ),
          )
          .map((model) => {
            return {
              ...model,
              deletable: { value: true, reason: "" },
              editable: { value: true, reason: "" },
            };
          });
      }
    }

    const datasetModels = [
      ...previousModelsForPreview,
      ...userSelectedModelsForPreview,
      ...localModels,
    ];

    // TODO: integrate a general way to handle model dependencies using the dependencies field in the model list
    // For now, we are hardcoding the dependency between VL-OBJECT-DETECTOR and VL-OBJECT-CAPTIONER since it's only
    // required for this use case
    const hasObjectCaption = !!datasetModels.find(
      (model) => model.type === "CAPTION_OBJECTS",
    );
    const previewDatasetModels = datasetModels.map((model) => {
      if (model.deletable.value) {
        // if the model is not deletable, we don't need to check for dependencies
        const deletionEnabled =
          !hasObjectCaption || model.type !== "OBJECT_DETECTION";
        return {
          ...model,
          deletable: {
            value: deletionEnabled,
            reason: deletionEnabled
              ? ""
              : "Please remove VL-OBJECT-CAPTIONER first",
          },
          editable: { value: true, reason: "" },
        };
      } else {
        return { ...model };
      }
    });

    // dedup models
    // todo - preveent dedup models by changing userSelectedModels only (not previewModels which also includes previousDatasetEnrichmentModels)
    const dedupModelsForPreview = Array.from(
      new Set(
        previewDatasetModels.map(
          (model: EnrichmentModelForPreview) => model.id,
        ),
      ),
    ).map((id) => {
      const previewModel = previewDatasetModels.find((a) => a.id === id);
      return previewModel!;
    });

    return dedupModelsForPreview;
  },
);

export const getEnrichmentModelList = createSelector(
  getModelsList,
  getPreviewModels,
  getPreviousDatasetEnrichmentModels,
  (
    modelsList,
    selectedPreviewModels,
    previousDatasetEnrichmentModels,
  ): EnrichmentModelOption[] => {
    // TODO: integrate a general way to handle model dependencies using the dependencies field in the model list
    // For now, we are hardcoding the dependency between VL-OBJECT-DETECTOR and VL-OBJECT-CAPTIONER since it's only
    // required for this use case
    const hasObjectDetection = !!selectedPreviewModels.find(
      (model) => model.type === "OBJECT_DETECTION",
    );

    const updatedModelList = modelsList.map((model) => {
      const hasAllDependencies =
        hasObjectDetection || model.type !== "CAPTION_OBJECTS";

      const isPreviouslySelected = previousDatasetEnrichmentModels.find(
        (prevModel: EnrichmentModel) => prevModel.id === model.id,
      );

      const isReplaced = isPreviouslySelected?.replaced;

      const isSelected =
        selectedPreviewModels.some((prevModel) => prevModel.id === model.id) ||
        false;

      return {
        ...model,
        selectable: {
          value: hasAllDependencies,
          reason: hasAllDependencies
            ? ""
            : "VL-OBJECT-DETECTOR is required for VL-OBJECT-CAPTIONER. Please select VL-OBJECT-DETECTOR first.",
        },
        selectedStatus: isReplaced
          ? EnrichmentModelSelectedStatus.MODEL_NOT_SELECTED
          : isPreviouslySelected
            ? EnrichmentModelSelectedStatus.MODEL_PREVIOUSLY_SELECTED
            : isSelected
              ? EnrichmentModelSelectedStatus.MODEL_SELECTED
              : EnrichmentModelSelectedStatus.MODEL_NOT_SELECTED,
      };
    });

    return updatedModelList;
  },
);

export const getShowEnrichmentProgress = createSelector(
  state,
  (state): boolean => {
    return state.showEnrichmentProgress;
  },
);

export const getSelectedModelId = createSelector(
  state,
  (state): string | null => {
    return state.selectedModelId;
  },
);

export const getEnrichmentProgress = createSelector(state, (state): number => {
  return state.enrichmentProgress;
});

export const getEnrichmentStatus = createSelector(
  state,
  (state): EnrichmentStatus | null => {
    return state.enrichmentStatus;
  },
);

export const getEnrichedDataset = createSelector(
  getEnrichedDatasetId,
  getDatasets,
  (enrichedDatasetId, datasets) => {
    return datasets.find((dataset: any) => dataset.id === enrichedDatasetId);
  },
);

export const getEnrichedTargetDatasetId = createSelector(
  state,
  (state): string | null => {
    return state.enrichedTargetDatasetId;
  },
);

export const getAllEnrichmentModelsUsed = createSelector(
  getEnrichmentModelList,
  singleDatasetState,
  (modelList, singleDatasetState) => {
    const activeModelList = modelList.filter(
      (model) => model.status !== "Coming Soon",
    );
    let activeModelListIds = activeModelList.map((model) => model.id);
    let promptableModelIds = activeModelList
      .filter((model) => model.vqa_prompt)
      .map((model) => model.id);

    let enrichedModels: EnrichmentModel[] = Object.values(
      singleDatasetState.stats?.dataset?.enrichment_models?.enrichment_models ||
        {},
    );

    let enrichedModelIds = enrichedModels.map((model) => model.id);
    const allUsed = activeModelListIds.every((id) => {
      return enrichedModelIds.includes(id);
    });
    const allCorrectlyPrompted = enrichedModels.every((model) => {
      return (
        !promptableModelIds.includes(model.id) || model.metadata?.prompt !== ""
      );
    });
    return allUsed && allCorrectlyPrompted;
  },
);
